import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
import queries from "../queries";
import Swal from "sweetalert2";
import "../css/NewsletterModal.css";

function NewsletterModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  const [subscribeToNewsletter] = useMutation(
    queries.newsletter.SUBSCRIBE_TO_NEWSLETTER
  );

  useEffect(() => {
    // Don't show modal on certain pages
    const excludedPaths = ["/checkout", "/login", "/signup", "/cart"];
    const currentPath = location.pathname;

    if (excludedPaths.some((path) => currentPath.startsWith(path))) {
      return;
    }

    // Check if user has already seen the modal
    const hasSeenModal = localStorage.getItem("newsletterModalSeen");

    // Check if user dismissed and when (for "remind me later" functionality)
    const dismissedTime = localStorage.getItem("newsletterModalDismissed");
    const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours

    if (hasSeenModal === "true") {
      return; // User subscribed, don't show again
    }

    if (dismissedTime) {
      const timeSinceDismissed = Date.now() - parseInt(dismissedTime);
      if (timeSinceDismissed < oneDayInMs) {
        return; // Less than 24 hours since dismissal, don't show yet
      }
    }

    // Show modal after 15 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
      // Prevent body scroll when modal opens
      document.body.classList.add("modal-open");
    }, 15000); // 15 seconds

    return () => {
      clearTimeout(timer);
      // Clean up body class
      document.body.classList.remove("modal-open");
    };
  }, [location]);

  const handleClose = () => {
    setIsOpen(false);
    document.body.classList.remove("modal-open");
    // Mark modal as seen (user subscribed)
    localStorage.setItem("newsletterModalSeen", "true");
  };

  const handleDismiss = () => {
    setIsOpen(false);
    document.body.classList.remove("modal-open");
    // Show reminder after 24 hours
    localStorage.setItem("newsletterModalDismissed", Date.now().toString());
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || email.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please enter your email address",
      });
      return;
    }

    if (!validateEmail(email)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Email",
        text: "Please enter a valid email address",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data } = await subscribeToNewsletter({
        variables: { email: email.trim().toLowerCase() },
      });

      if (data?.subscribeToNewsletter?.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text:
            data.subscribeToNewsletter.message ||
            "Successfully subscribed to our newsletter!",
          confirmButtonColor: "#28a745",
        });
        handleClose();
      } else {
        throw new Error(
          data?.subscribeToNewsletter?.message || "Subscription failed"
        );
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      Swal.fire({
        icon: "error",
        title: "Subscription Failed",
        text:
          error.message ||
          "Unable to subscribe at this time. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="newsletter-modal-overlay" onClick={handleDismiss}>
        <div
          className="newsletter-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="newsletter-modal-close"
            onClick={handleDismiss}
            aria-label="Close"
          >
            <i className="fa fa-times"></i>
          </button>

          <div className="newsletter-modal-icon">
            <i className="fa fa-envelope"></i>
          </div>

          <h2 className="newsletter-modal-title">Stay in the Loop! 🌿</h2>
          <p className="newsletter-modal-description">
            Get exclusive deals, new product updates, and special promotions
            delivered straight to your inbox. Join our community today!
          </p>

          <form className="newsletter-modal-form" onSubmit={handleSubmit}>
            <div className="newsletter-modal-input-wrapper">
              <i className="fa fa-envelope-o"></i>
              <input
                type="email"
                className="newsletter-modal-input"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                autoFocus
              />
            </div>

            <button
              type="submit"
              className="newsletter-modal-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Subscribing...
                </>
              ) : (
                <>
                  <i className="fa fa-check mr-2"></i>
                  Subscribe Now
                </>
              )}
            </button>

            <button
              type="button"
              className="newsletter-modal-skip"
              onClick={handleDismiss}
              disabled={isSubmitting}
            >
              No thanks, maybe later
            </button>
          </form>

          <p className="newsletter-modal-privacy">
            <i className="fa fa-lock mr-1"></i>
            We respect your privacy. Unsubscribe anytime.
          </p>

          <div className="newsletter-modal-benefits">
            <div className="benefit-item">
              <i className="fa fa-gift"></i>
              <span>Exclusive Offers</span>
            </div>
            <div className="benefit-item">
              <i className="fa fa-star"></i>
              <span>New Products</span>
            </div>
            <div className="benefit-item">
              <i className="fa fa-percent"></i>
              <span>Special Deals</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewsletterModal;
